import { reportPlatformError, runWithErrorReporting } from "$root/api/error-reporting";
import { ToastableError } from "$root/utils";
import { spawnInstrumentAddersDialog } from "$root/functional-areas/instruments-editor/spawn/instrument-adder";
import type { Instrument } from "$root/functional-areas/instruments-editor/const";
import { instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { platformToast } from "$root/notification-system/toast";
import { readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { useMandatoryContext } from "@mdotm/utils/react-extra";
import { useCallback } from "react";
import { parallelize } from "$root/utils/promise";
import {
	canTriggerUpdateComposition,
	isAumCommitCandidate,
	isCompositionExactly100,
	isUpdatableInvestmentStatus,
} from "../common";
import { resetComposition, syncComposition } from "../components/composition-editor/persistence";
import { deleteInvestments } from "../deletion";
import {
	type PortfolioLabCompositionInstrument,
	type PortfolioLabCompositionPortfolio,
	type PortfolioLabInvestmentItem,
	PortfolioLabContext,
} from "../ctx";
import { portfolioLabRepository } from "../repository";
import { spawnInvestmentPickerDialog } from "../components/investment-picker";
import { spawnUpdateCompositionConfirmDialog } from "../components/UpdateCompositionConfirmDialog";
import { customerDecimalPlaces, user$ } from "$root/functional-areas/user";
import { aclByArea } from "$root/functional-areas/acl/checkers/all";
import { trackPortfolioLabSessionChange } from "../analytics";

export function useCompositionEditor() {
	const ctx = useMandatoryContext(PortfolioLabContext);

	const onReset = useCallback(
		(virtualUuid: string) => {
			let updated: PortfolioLabInvestmentItem | undefined;
			writeAtom(ctx.investments$, (items) => {
				const next = items.map(
					(i): PortfolioLabInvestmentItem =>
						i.virtualUuid === virtualUuid
							? {
									...i,
									currentComposition: i.initialComposition,
									aum: i.initialAum,
								}
							: i,
				);
				updated = next.find((i) => i.virtualUuid === virtualUuid);
				return next;
			});
			if (updated) {
				resetComposition(ctx, virtualUuid).catch((error: unknown) => {
					reportPlatformError(error, "ERROR", "portfolio", {
						message: `Unable to reset the lab composition (${virtualUuid})`,
					});
				});
				trackPortfolioLabSessionChange(ctx, "resetColumn");
			}
		},
		[ctx],
	);

	const onRemove = useCallback(
		(virtualUuid: string) => {
			// `deleteInvestments` cancels this column's in-flight compute and then deletes on the
			// investment's own lane, so the DELETE runs AFTER any composition write still flushing for
			// this uuid without waiting out the (long) compute. The atom removal stays optimistic/
			// immediate; run-time-resolving writes chained behind the delete no-op (the item is gone from
			// the atom), so nothing fires at the now-deleted uuid.
			void deleteInvestments(ctx, [virtualUuid]);
			writeAtom(ctx.investments$, (items) => items.filter((i) => i.virtualUuid !== virtualUuid));
			trackPortfolioLabSessionChange(ctx, "removeColumn");
		},
		[ctx],
	);

	const onAddPortfolio = useCallback(
		(virtualUuid: string) => {
			const investment = readAtom(ctx.investments$).find((i) => i.virtualUuid === virtualUuid);
			if (!investment) {
				return;
			}
			spawnInvestmentPickerDialog({
				title: "Portfolio",
				multiple: true,
				compositionProvider: async (search) => {
					const availablePortfolios = await portfolioRepository.editorAvailablePortfolios.fetchQuery(
						investment.uuid,
						"INVESTMENT",
					);
					// Exclude portfolios already nested in this investment so they can't be added twice.
					const existingPortfolioUuids = new Set(
						investment.currentComposition.flatMap((c) => (c.type === "portfolio" ? [c.uuid] : [])),
					);
					return (availablePortfolios ?? []).flatMap((item) => {
						const { uuid, name, status, canUseAsMixedPortfolio } = item;
						if (!uuid || !name || !status || !canUseAsMixedPortfolio) {
							return [];
						}
						if (existingPortfolioUuids.has(uuid)) {
							return [];
						}
						if (search && !name.toLowerCase().includes(search.toLowerCase())) {
							return [];
						}
						return [{ uuid, name, status }];
					});
				},
				onSubmitAsync: async (entities) => {
					const newPortfolios = await parallelize(
						entities.map((entity) => async (): Promise<PortfolioLabCompositionPortfolio> => {
							const nestedComposition = await portfolioLabRepository.loadComposition.fetchQuery(
								"INVESTMENT",
								entity.uuid,
							);
							return {
								type: "portfolio",
								uuid: entity.uuid,
								name: entity.name,
								weight: 0,
								monetaryWeight: 0,
								instruments: nestedComposition
									.filter((c): c is PortfolioLabCompositionInstrument => c.type === "instrument")
									.map((c) => ({ tickerName: c.tickerName, weight: 0, monetaryWeight: 0 })),
							};
						}),
					);
					const portfolioInstrumentTickers = newPortfolios.flatMap((p) => p.instruments.map((i) => i.tickerName));
					const newPortfolioTickerInfoPool =
						await instrumentsRepository.userClassifications.fetchQuery(portfolioInstrumentTickers);
					writeAtom(ctx.investments$, (items) => {
						const next = items.map(
							(i): PortfolioLabInvestmentItem =>
								i.virtualUuid === virtualUuid
									? {
											...i,
											currentComposition: [...newPortfolios, ...i.currentComposition],
											tickerInfoPool: i.tickerInfoPool.mergeDeep(newPortfolioTickerInfoPool),
										}
									: i,
						);
						return next;
					});
					trackPortfolioLabSessionChange(ctx, "addPortfolioToColumn");
					// The atom write above re-derives the grid's rowData (new rows included);
					// persistence goes through this investment's write lane with failure surfacing (T6).
					// The column header reads `investments$` itself, so a failed sync surfaces there
					// without an imperative refresh.
					void syncComposition(ctx, virtualUuid);
				},
			});
		},
		[ctx],
	);

	const onAddInstrument = useCallback(
		(virtualUuid: string) => {
			const investment = readAtom(ctx.investments$).find((i) => i.virtualUuid === virtualUuid);
			if (!investment) {
				return;
			}
			const instrumentsAlreadySelected = investment.currentComposition.flatMap((c): Instrument[] => {
				if (c.type === "instrument") {
					return [{ tickerName: c.tickerName, id: c.tickerName, instrumentType: "INSTRUMENT" } as Instrument];
				}
				return [];
			});
			spawnInstrumentAddersDialog({
				entity: "INVESTMENT",
				uuid: investment.uuid,
				instrumentsAlreadySelected,
				onSubmitAsync: async (tickerNames) => {
					const newInstruments: PortfolioLabCompositionInstrument[] = tickerNames.map((tickerName) => ({
						type: "instrument",
						tickerName,
						weight: 0,
						monetaryWeight: 0,
					}));
					const newInstrumentsTickerInfoPool = await instrumentsRepository.userClassifications.fetchQuery(
						newInstruments.map((inst) => inst.tickerName),
					);
					writeAtom(ctx.investments$, (items) => {
						const next = items.map((inv): PortfolioLabInvestmentItem => {
							if (inv.virtualUuid === virtualUuid) {
								return {
									...inv,
									currentComposition: [...newInstruments, ...inv.currentComposition],
									tickerInfoPool: inv.tickerInfoPool.mergeDeep(newInstrumentsTickerInfoPool),
								};
							}
							return inv;
						});
						return next;
					});
					trackPortfolioLabSessionChange(ctx, "addInstrumentToColumn");
					// The atom write above re-derives the grid's rowData (new rows included);
					// persistence goes through this investment's write lane with failure surfacing (T6).
					// The column header reads `investments$` itself, so a failed sync surfaces there
					// without an imperative refresh.
					void syncComposition(ctx, virtualUuid);
				},
			});
		},
		[ctx],
	);

	const onUpdateComposition = useCallback(
		async (virtualUuid: string) => {
			const investment = readAtom(ctx.investments$).find((i) => i.virtualUuid === virtualUuid);
			if (!investment || !canTriggerUpdateComposition(investment)) {
				return;
			}
			const hasPreconditions = await validateUpdateCompositionPreconditions(investment);
			if (!hasPreconditions) {
				return;
			}

			// Whether an AUM write will be attempted, computed before attribute resolution — the dialog
			// copy must announce the AUM update whenever the commit will try one.
			const willUpdateAum = isAumCommitCandidate(investment);
			void spawnUpdateCompositionConfirmDialog({ investmentName: investment.name, willUpdateAum }).promise.then(
				(confirmed) => {
					if (!confirmed) {
						return;
					}
					// Re-read + re-gate: the live status (or a concurrent commit) may have changed while the
					// confirm dialog was open.
					const itemToSave = readAtom(ctx.investments$).find((i) => i.virtualUuid === virtualUuid);
					if (!itemToSave || !canTriggerUpdateComposition(itemToSave)) {
						return;
					}
					// The column group header reads `investments$`, so the saving loader appears from this
					// write alone — an imperative refreshHeader() would remount the header (and close the
					// very menu this action was started from). See LabPortfolioGroupHeader.
					writeAtom(ctx.investments$, (items) =>
						items.map((i) => (i.virtualUuid === virtualUuid ? { ...i, compositionSaveStatus: "saving" } : i)),
					);
					void ctx.investmentWriteLanes.run(virtualUuid, async () => {
						const latest = readAtom(ctx.investments$).find((i) => i.virtualUuid === virtualUuid);
						if (!latest) {
							return;
						}
						try {
							// One atomic save: the composition and (for investments/proposals with a session AUM)
							// the AUM custom attribute travel in the same request — both apply or neither does.
							await commitRealCompositionAndAum(latest);
							// Rebase the Reset baseline to the just-committed composition, and start the settle
							// wait: the real investment is now recalculating, so the virtual re-sync (reset +
							// re-push) is deferred until its live status settles (useRealInvestmentStatusSync).
							writeAtom(ctx.investments$, (items) =>
								items.map((i) =>
									i.virtualUuid === virtualUuid
										? { ...i, initialComposition: i.currentComposition, commitSettleStatus: "waiting" }
										: i,
								),
							);
							platformToast({
								icon: "Portfolio-update",
								severity: "success",
								children: "Composition updated successfully.",
							});
						} finally {
							writeAtom(ctx.investments$, (items) =>
								items.map((i) => (i.virtualUuid === virtualUuid ? { ...i, compositionSaveStatus: "idle" } : i)),
							);
						}
					});
				},
			);
		},
		[ctx.investments$, ctx.investmentWriteLanes],
	);

	return { onReset, onRemove, onAddPortfolio, onAddInstrument, onUpdateComposition };
}

const labEntityToAclEntity = {
	INVESTMENT: "portfolio",
	INVESTMENT_PROPOSAL: "portfolio",
	BENCHMARK: "benchmark",
	REFERENCE: "targetPortfolio",
} satisfies Record<PortfolioLabInvestmentItem["type"], keyof typeof aclByArea>;

/**
 * Gates an "Update composition" commit: edit ACL on the real investment, an updatable status, and
 * a composition totalling exactly 100%. Toasts the reason and returns `false` when the commit must
 * not proceed.
 */
async function validateUpdateCompositionPreconditions(investment: PortfolioLabInvestmentItem): Promise<boolean> {
	const summary = await portfolioLabRepository.summary.fetchQuery(investment.uuid, investment.type);
	const hasEditPermission = aclByArea[labEntityToAclEntity[investment.type]].canEditComposition(
		readAtom(user$).id,
		summary.richAcl?.acl ?? [],
	);
	if (!hasEditPermission) {
		platformToast({
			icon: "Icon-full-error",
			severity: "error",
			children: "Can't update this portfolio — you don't have edit access. Request permission to proceed.",
		});
		return false;
	}
	if (!summary.status || !isUpdatableInvestmentStatus(summary.status)) {
		platformToast({
			icon: "Icon-full-error",
			severity: "error",
			children: "This portfolio can't be updated yet. It needs to be in Ready or Accepted status first.",
		});
		return false;
	}
	if (!isCompositionExactly100(investment.currentComposition, customerDecimalPlaces(readAtom(user$)))) {
		platformToast({
			icon: "Icon-full-error",
			severity: "error",
			children: "The composition must total exactly 100% before it can be updated.",
		});
		return false;
	}
	return true;
}

/**
 * Commits the lab composition — and, for investments/proposals with a session AUM, the AUM custom
 * attribute in the same atomic save request (see `repository.updateRealInvestmentComposition`) — to
 * the real investment. Throws on failure — the "Failed to update the composition" toast has already
 * been shown via `runWithErrorReporting`, so callers only need to skip their success path. A failure
 * anywhere (including the AUM attribute pre-fetch) means nothing was changed on the server.
 *
 * Deliberately does NOT re-sync the virtual copy: the real investment starts recalculating on
 * commit, so `resetVirtualInvestment` is deferred until its live status settles back to an
 * updatable one (see `useRealInvestmentStatusSync`'s settle machine).
 */
async function commitRealCompositionAndAum(latest: PortfolioLabInvestmentItem): Promise<void> {
	await runWithErrorReporting(
		async () => {
			try {
				await portfolioLabRepository.updateRealInvestmentComposition(latest);
			} catch (error) {
				throw new ToastableError("Failed to update the composition. Please try again.", {
					cause: error,
					icon: "Icon-full-error",
				});
			}
		},
		{
			area: "portfolio",
			attemptedOperation: {
				message: `Unable to update composition (${latest.virtualUuid})`,
			},
		},
	);
}
